
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./BankBook.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserProfile } from "../../redux/slices/branch/userProfileSlice";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "https://payyug.in/api";

const BankBook = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50,100];

  const [ledger, setLedger] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [openingBalance,setOpeningBalance]=useState("");
  const [closingBalance,setClosingBalance]=useState("");

  const [bankBookData, setBankBookData] = useState([]);
  const [branchLedgerList, setBranchLedgerList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const { profile } = useSelector((state) => state.userProfile);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  // Fetch ledgers when profile metadata is ready
  const fetchBranchLedgers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/branch-ledger/get-all`, {
        params: {
          superAdminId: profile?.superAdminId,
          limit: 1000,
          status: "Active",
        },
      });
      setBranchLedgerList(res?.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong!");
    }
  }, [profile?.superAdminId]);

  useEffect(() => {
    if (profile?.superAdminId) {
      fetchBranchLedgers();
    }
  }, [profile?.superAdminId, fetchBranchLedgers]);

  // Fetch system records
  const fetchCashBookData = useCallback(async () => {
    if (!fromDate || !toDate) return;

    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_URL}/report/bank-book`, {
        params: {
          superAdminId: profile?.superAdminId,
          branchId: profile?.branchId,
          fromDate: fromDate,
          toDate: toDate,
          bankLedgerId: ledger,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setOpeningBalance(res?.data?.openingBalance)
      setClosingBalance(res?.data?.closingBalance)
      const dataPayload = res?.data?.data || res?.data || [];
      setBankBookData(dataPayload);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data?.message || "Failed to fetch cash book records";
      toast.error(errMsg);
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, ledger, profile]);

  /* ================= FILTER ================= */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!fromDate) {
      toast.warn("Please select From Date");
      return;
    }
    if (!toDate) {
      toast.warn("Please select To Date");
      return;
    }
    setHasSearched(true);
    fetchCashBookData();
  };

  /* ================= CLIENT SIDE SEARCH + PAGINATION ================= */
  const filteredData = useMemo(() => {
    if (!bankBookData || !Array.isArray(bankBookData)) return [];
    return bankBookData.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [bankBookData, searchQuery]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="bankBook-container">
      <h1 className="bankBook-heading">Bank Book Management</h1>

      {/* Overview Form Section */}
      <section className="bankBook-top">
        <form className="bankBook-form" onSubmit={handleSubmit}>
          <div className="bankBook-form-section">
            <div className="bankBook-form-input-fields">
              <label htmlFor="ledger-select">Select Ledger</label>
              <select
                id="ledger-select"
                name="ledger"
                value={ledger}
                onChange={(e) => setLedger(e.target.value)}
              >
                <option value="">All Bank Ledgers</option>
                {branchLedgerList.map((l) => (
                  <option key={l.id || l._id} value={l.id || l._id}>
                    {l?.ledger?.name || l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="bankBook-form-input-fields">
              <label htmlFor="from-date">From Date</label>
              <input
                id="from-date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="bankBook-form-input-fields">
              <label htmlFor="to-date">To Date</label>
              <input
                id="to-date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
          <div className="bankBook-form-submit-btn">
            <button type="submit" disabled={loading}>
              {loading ? "Loading..." : "Get Records"}
            </button>
          </div>
        </form>
      </section>

      {/* Data Presentation Area */}
      {hasSearched && (
        <div className="bankBook-report-container">
          <h2>Bank Book Records & Summary</h2>
          
          <div className="bankBook-report-filter-div">
            <input
              type="text"
              className="search-input"
              placeholder="Search statements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="bankBook-report-filter">
              <label>Show </label>
              <select onChange={handleItemsPerPageChange} value={itemsPerPage}>
                {ITEMS_PER_PAGE_OPTIONS.map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              <span> entries</span>
            </div>
          </div>

          {error && <p className="error-message-banner">{error}</p>}

          <div className="bankBook-report-table-container">
            <table className="bankBook-report-table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Voucher No</th>
                  <th>Voucher Type</th>
                  <th>Date</th>
                  <th>Debit (In)</th>
                  <th>Credit (Out)</th>
                  <th>Balance</th>
                  <th>Opening Balance</th>
                  <th>Closing Balance</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={item.id || item._id || index}>
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>{item.voucherNo || "N/A"}</td>
                      <td>{item.voucherType || "N/A"}</td>
                      <td>{item?.date?new Date(item?.date).toLocaleDateString("en-GB"): "N/A"}</td>
                      <td className="amount-debit">{item.debit ? `₹${item.debit}` : "0.00"}</td>
                      <td className="amount-credit">{item.credit ? `₹${item.credit}` : "0.00"}</td>
                      <td className="amount-balance"><strong>₹{item.balance ?? "0.00"}</strong></td>
                      <td className="amount-balance"><strong>₹{openingBalance ?? "0.00"}</strong></td>
                      <td className="amount-balance"><strong>₹{closingBalance ?? "0.00"}</strong></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="no-results-td">
                      No logs or statement entries match the criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Component Controls */}
          {filteredData.length > itemsPerPage && (
            <div className="bankBook-report-pagination-div">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                ⬅ Prev
              </button>

              <span>
                Page <strong>{currentPage}</strong> of {totalPages}
              </span>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              >
                Next ➡
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BankBook;